/**
 * SupperNhanVienNhapLieu dashboard — high-level view of the teacher
 * submission queue per course, and the quota-allocation tool.
 *
 * Left column: bucket of each teacher (+ hang) with queue counts and the
 * current quota (if any) for the selected course. Click a row to open a
 * detail modal with all that teacher's active submissions. Click "Đặt
 * quota" to open the allocator modal.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import {
  fetchCourses,
  supperQueueByTeacherApi,
  supperQueueForTeacherApi,
  supperAllocateQuotaApi,
  supperDeallocateQuotaApi,
  type SupperNvnlTeacherBucket,
  type TtSubmission,
} from '../services/trungTamApi';
import type { TtCourse } from '../types';
import ScanMethodBadge from './ScanMethodBadge';

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch { return '—'; }
};

const SupperNvnlDashboard: React.FC = () => {
  const [courses, setCourses] = useState<TtCourse[]>([]);
  const [courseId, setCourseId] = useState<number | null>(null);
  const [hangFilter, setHangFilter] = useState<string>('');
  const [buckets, setBuckets] = useState<SupperNvnlTeacherBucket[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailBucket, setDetailBucket] = useState<SupperNvnlTeacherBucket | null>(null);
  const [detailRows, setDetailRows] = useState<TtSubmission[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [quotaTarget, setQuotaTarget] = useState<SupperNvnlTeacherBucket | null>(null);
  const [quotaValue, setQuotaValue] = useState<number>(0);
  const [quotaNote, setQuotaNote] = useState<string>('');
  const [quotaBusy, setQuotaBusy] = useState(false);

  const selectedCourse = useMemo(
    () => courses.find(c => c.id === courseId) || null,
    [courses, courseId],
  );

  // Fetch courses on mount — default select the oldest open course.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetchCourses({ status: 'open' });
        if (res.EC === 0 && Array.isArray(res.DT)) {
          setCourses(res.DT);
          if (!courseId && res.DT.length > 0) setCourseId(res.DT[0].id);
        }
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params: { courseId?: number; hang?: string } = {};
      if (courseId) params.courseId = courseId;
      if (hangFilter) params.hang = hangFilter;
      const res = await supperQueueByTeacherApi(params);
      if (res.EC === 0 && Array.isArray(res.DT)) setBuckets(res.DT);
      else setBuckets([]);
    } catch {
      setBuckets([]);
    } finally { setLoading(false); }
  }, [courseId, hangFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  const openDetail = async (b: SupperNvnlTeacherBucket) => {
    setDetailBucket(b);
    setDetailRows([]);
    setDetailLoading(true);
    try {
      const res = await supperQueueForTeacherApi(b.submittedBy, { hang: b.hang });
      if (res.EC === 0 && Array.isArray(res.DT)) setDetailRows(res.DT);
    } catch { /* ignore */ } finally { setDetailLoading(false); }
  };

  const openQuota = (b: SupperNvnlTeacherBucket) => {
    if (!courseId) { toast.warn('Hãy chọn khóa đích trước'); return; }
    setQuotaTarget(b);
    setQuotaValue(b.quota?.maxRecords ?? b.totalActive);
    setQuotaNote(b.quota?.note ?? '');
  };

  const submitQuota = async () => {
    if (!quotaTarget || !courseId) return;
    if (!Number.isFinite(quotaValue) || quotaValue < 0) {
      toast.warn('Số lượng không hợp lệ');
      return;
    }
    setQuotaBusy(true);
    try {
      const resp = await supperAllocateQuotaApi({
        teacherId: quotaTarget.submittedBy,
        courseId,
        hang: quotaTarget.hang,
        maxRecords: quotaValue,
        note: quotaNote.trim() || undefined,
      });
      if (resp.EC === 0) {
        toast.success(`Đã đặt hạn ngạch ${quotaValue} hồ sơ cho ${quotaTarget.submitter?.username || ''}`);
        setQuotaTarget(null);
        await refresh();
      } else {
        toast.warn(resp.EM || 'Không đặt được hạn ngạch');
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { EM?: string } } };
      toast.error(e?.response?.data?.EM || 'Lỗi server');
    } finally { setQuotaBusy(false); }
  };

  const removeQuota = async (quotaId: number) => {
    if (!window.confirm('Hủy hạn ngạch này? Giáo viên sẽ không thể nhập hồ sơ vào khóa cho tới khi đặt lại.')) return;
    try {
      const resp = await supperDeallocateQuotaApi(quotaId);
      if (resp.EC === 0) {
        toast.success('Đã hủy hạn ngạch');
        await refresh();
      } else toast.warn(resp.EM || 'Không hủy được');
    } catch {
      toast.error('Lỗi server');
    }
  };

  return (
    <div className="tt-supper-nvnl">
      <div className="tt-supper-nvnl__toolbar">
        <label className="tt-supper-nvnl__select">
          <span>Khóa đích</span>
          <select
            value={courseId ?? ''}
            onChange={e => setCourseId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">— Chọn khóa —</option>
            {courses.map(c => (
              <option key={c.id} value={c.id}>
                {c.name} (Hạng {c.hang || '—'})
              </option>
            ))}
          </select>
        </label>
        <label className="tt-supper-nvnl__select">
          <span>Hạng</span>
          <select value={hangFilter} onChange={e => setHangFilter(e.target.value)}>
            <option value="">Tất cả</option>
            {Array.from(new Set(courses.map(c => c.hang).filter(Boolean))).map(h => (
              <option key={h} value={h!}>{h}</option>
            ))}
          </select>
        </label>
        <button type="button" className="tt-btn tt-btn--outline tt-btn--sm" onClick={refresh}>
          <i className="material-icons">refresh</i> Làm mới
        </button>
      </div>

      {selectedCourse && (
        <div className="tt-supper-nvnl__course-banner">
          <i className="material-icons">school</i>
          <strong>{selectedCourse.name}</strong>
          <span className="tt-queue__hang">Hạng {selectedCourse.hang}</span>
        </div>
      )}

      {loading ? (
        <div className="tt-queue__empty">Đang tải...</div>
      ) : buckets.length === 0 ? (
        <div className="tt-queue__empty">
          <i className="material-icons">inbox</i>
          <p>Không có giáo viên nào đang có hồ sơ chờ</p>
        </div>
      ) : (
        <div className="tt-queue__wrap">
          <table className="tt-queue__table tt-supper-nvnl__table">
            <thead>
              <tr>
                <th>Giáo viên</th>
                <th>Hạng</th>
                <th>Tổng</th>
                <th>Chờ</th>
                <th>Đã tiếp nhận</th>
                <th>Trì hoãn</th>
                <th>Nộp cũ nhất</th>
                <th>Hạn ngạch</th>
                <th className="tt-queue__th-actions">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {buckets.map(b => {
                const hasQuota = !!b.quota;
                const quotaReached = hasQuota && b.enteredCount >= (b.quota?.maxRecords || 0);
                return (
                  <tr
                    key={`${b.submittedBy}-${b.hang}`}
                    className="tt-queue__row"
                    onClick={() => openDetail(b)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div className="tt-queue__name">{b.submitter?.username || `#${b.submittedBy}`}</div>
                      <div className="tt-queue__cccd">
                        STT cũ nhất: #{b.oldestSeqNo}
                      </div>
                    </td>
                    <td><span className="tt-queue__hang">{b.hang}</span></td>
                    <td><strong>{b.totalActive}</strong></td>
                    <td>{b.pendingCount}</td>
                    <td>{b.acceptedCount}</td>
                    <td>{b.deferredCount > 0 ? (
                      <span className="tt-scan-badge tt-scan-badge--ocr tt-scan-badge--compact">
                        <i className="material-icons">pause_circle</i>
                        {b.deferredCount}
                      </span>
                    ) : '0'}</td>
                    <td className="tt-queue__dt">{formatDate(b.oldestSubmittedAt)}</td>
                    <td>
                      {!courseId ? (
                        <span className="tt-supper-nvnl__quota-empty">Chọn khóa</span>
                      ) : hasQuota ? (
                        <span className={`tt-supper-nvnl__quota-pill${quotaReached ? ' tt-supper-nvnl__quota-pill--full' : ''}`}>
                          {b.enteredCount}/{b.quota!.maxRecords}
                        </span>
                      ) : (
                        <span className="tt-supper-nvnl__quota-empty">Chưa đặt</span>
                      )}
                    </td>
                    <td className="tt-queue__actions" onClick={e => e.stopPropagation()}>
                      <button
                        type="button"
                        className="tt-btn tt-btn--success tt-btn--sm"
                        onClick={() => openQuota(b)}
                        disabled={!courseId}
                      >
                        <i className="material-icons">rule</i>
                        {hasQuota ? 'Sửa hạn ngạch' : 'Đặt hạn ngạch'}
                      </button>
                      {hasQuota && (
                        <button
                          type="button"
                          className="tt-btn tt-btn--outline tt-btn--sm tt-queue__reject-btn"
                          onClick={() => removeQuota(b.quota!.id)}
                        >
                          <i className="material-icons">close</i>
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail modal — all active submissions for the clicked teacher */}
      {detailBucket && createPortal(
        <div className="tt-queue-modal__overlay" onClick={() => setDetailBucket(null)}>
          <div className="tt-queue-modal tt-queue-modal--detail" onClick={e => e.stopPropagation()}>
            <div className="tt-queue-detail__header">
              <div className="tt-queue-detail__head-main">
                <div className="tt-queue-detail__head-text">
                  <h3>{detailBucket.submitter?.username || `#${detailBucket.submittedBy}`}</h3>
                  <div className="tt-queue-detail__chips">
                    <span className="tt-queue-detail__chip tt-queue-detail__chip--info">Hạng {detailBucket.hang}</span>
                    <span className="tt-queue-detail__chip tt-queue-detail__chip--neutral">
                      {detailBucket.totalActive} hồ sơ đang hoạt động
                    </span>
                    {detailBucket.deferredCount > 0 && (
                      <span className="tt-queue-detail__chip tt-queue-detail__chip--warn">
                        <i className="material-icons">pause_circle</i>
                        {detailBucket.deferredCount} trì hoãn
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button className="tt-queue__detail-close" onClick={() => setDetailBucket(null)}>
                <i className="material-icons">close</i>
              </button>
            </div>

            <div className="tt-queue-detail__body">
              {detailLoading ? (
                <div className="tt-queue__empty">Đang tải...</div>
              ) : detailRows.length === 0 ? (
                <div className="tt-queue__empty">Không có hồ sơ</div>
              ) : (
                <table className="tt-queue__table">
                  <thead>
                    <tr>
                      <th>STT</th>
                      <th>Họ tên / CCCD</th>
                      <th>Nguồn</th>
                      <th>Trạng thái</th>
                      <th>Ngày nộp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailRows.map(r => (
                      <tr key={r.id}>
                        <td><strong>#{r.seqNo}</strong></td>
                        <td>
                          <div className="tt-queue__name">{r.hoTen}</div>
                          <div className="tt-queue__cccd">{r.soCCCD || '—'}</div>
                        </td>
                        <td><ScanMethodBadge method={r.scanMethod} size="compact" /></td>
                        <td>
                          <span className={`tt-scan-badge tt-scan-badge--${r.status === 'deferred' ? 'ocr' : r.status === 'accepted' ? 'qr' : 'manual'} tt-scan-badge--compact`}>
                            {r.status === 'pending' ? 'Chờ' :
                             r.status === 'accepted' ? 'Đã tiếp nhận' :
                             r.status === 'deferred' ? 'Trì hoãn' :
                             r.status === 'entered' ? 'Đã vào khóa' : 'Từ chối'}
                          </span>
                        </td>
                        <td className="tt-queue__dt">{formatDate(r.submittedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Quota allocator modal */}
      {quotaTarget && courseId && createPortal(
        <div className="tt-queue-modal__overlay" onClick={() => !quotaBusy && setQuotaTarget(null)}>
          <div className="tt-queue-modal" onClick={e => e.stopPropagation()}>
            <h3>
              Đặt hạn ngạch cho {quotaTarget.submitter?.username || `#${quotaTarget.submittedBy}`}
            </h3>
            <p className="tt-queue-modal__hint">
              Hạng <strong>{quotaTarget.hang}</strong> · Khóa <strong>{selectedCourse?.name}</strong>
              {' · '}Giáo viên hiện có <strong>{quotaTarget.totalActive}</strong> hồ sơ đang chờ
              {quotaTarget.enteredCount > 0 && (
                <> (đã vào khóa: <strong>{quotaTarget.enteredCount}</strong>)</>
              )}.
            </p>
            <label>
              <span>Số hồ sơ tối đa cho phép <b className="tt-required">*</b></span>
              <input
                type="number"
                min={quotaTarget.enteredCount}
                max={quotaTarget.totalActive + quotaTarget.enteredCount}
                value={quotaValue}
                onChange={e => setQuotaValue(Number(e.target.value))}
              />
            </label>
            <label>
              <span>Ghi chú (tùy chọn)</span>
              <textarea
                rows={2}
                maxLength={500}
                value={quotaNote}
                onChange={e => setQuotaNote(e.target.value)}
                placeholder="Ví dụ: ưu tiên nhóm nâng hạng..."
              />
            </label>
            <div className="tt-queue-modal__actions">
              <button type="button" className="tt-btn tt-btn--outline" onClick={() => setQuotaTarget(null)} disabled={quotaBusy}>
                Hủy
              </button>
              <button type="button" className="tt-btn tt-btn--success" onClick={submitQuota} disabled={quotaBusy}>
                <i className="material-icons">check</i> Xác nhận
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default SupperNvnlDashboard;
